import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { IKnmPmFormRequirementsNpa } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';

import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IKnmErknmFormRequirementNpa } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import styles from './KnmViolatedRequirementsNpaModal.module.scss';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';

type RequirementsNpaType = IKnmErknmFormRequirementNpa[] | IKnmPmFormRequirementsNpa[] | undefined;

interface IErknmViolatedRequirementsNpaModalProps {
  requirementsNpa: RequirementsNpaType;
  visible: boolean;
  onClose: () => void;
  onSubmit: (items: (IKnmErknmFormRequirementNpa | IKnmPmFormRequirementsNpa)[]) => void;
}

export const KnmViolatedRequirementsNpaModal = ({
  requirementsNpa,
  visible,
  onClose,
  onSubmit,
}: IErknmViolatedRequirementsNpaModalProps) => {
  const [selectedRequirementsNpa, setSelectedRequirementsNpa] = useState<
    (IKnmErknmFormRequirementNpa | IKnmPmFormRequirementsNpa)[]
  >([]);
  const [searchString, setSearchString] = useState<string>('');

  useEffect(() => {
    if (visible) setSelectedRequirementsNpa([]);
  }, [visible]);

  const isRequirementsNpaEquals = (
    val1: IKnmErknmFormRequirementNpa | IKnmPmFormRequirementsNpa,
    val2: IKnmErknmFormRequirementNpa | IKnmPmFormRequirementsNpa,
  ): boolean => {
    return val1.npa.id === val2.npa.id && val1.structuralUnit.id === val2.structuralUnit.id;
  };

  const isSelected = (value: IKnmErknmFormRequirementNpa | IKnmPmFormRequirementsNpa): boolean => {
    return !!selectedRequirementsNpa.find(item => isRequirementsNpaEquals(item, value));
  };

  const handleSelect = (value: IKnmErknmFormRequirementNpa | IKnmPmFormRequirementsNpa) => {
    if (isSelected(value)) {
      setSelectedRequirementsNpa(selectedRequirementsNpa.filter(item => !isRequirementsNpaEquals(item, value)));
    } else setSelectedRequirementsNpa([...selectedRequirementsNpa, value]);
  };

  const handleSubmit = () => {
    onSubmit(selectedRequirementsNpa);
    onClose();
    setSearchString('');
  };

  const onSearch = useCallback((val: string) => {
    setSearchString(val);
    setSelectedRequirementsNpa([]);
  }, []);

  const lowerCaseSearch = searchString.toLowerCase();
  // @ts-ignore
  const filteredRequirementsNpa = (requirementsNpa || [])?.filter((el: IKnmErknmFormRequirementNpa) => {
    return (
      el.npa.number?.toLowerCase().includes(lowerCaseSearch) ||
      el.npa.title?.toLowerCase().includes(lowerCaseSearch) ||
      el.structuralUnit.title?.toLowerCase().includes(lowerCaseSearch)
    );
  }) as RequirementsNpaType;

  return (
    <Modal
      visible={visible}
      onClose={() => {
        onClose();
        setSearchString('');
      }}
    >
      <ModalHeader title="Добавление НПА и его структурной единицы" />
      <ModalBody overflow="scroll">
        <div className={styles.Search}>
          <TextInput
            id="searchString"
            name="searchString"
            placeholder="Введите номер или часть наименования НПА или его структурной единицы"
            clearable={!!searchString}
            onChange={e => onSearch(e.target.value)}
            onClear={() => onSearch('')}
            value={searchString}
          />
        </div>
        <Row>Выберите НПА и его структурную единицу, требование в котором было нарушено</Row>
        {filteredRequirementsNpa?.length ? (
          <>
            {filteredRequirementsNpa?.map((value, index) => {
              if (!value.npa || !value.structuralUnit) return null;

              if (value.inspectionResults?.find(i => i.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED)) {
                return null;
              }

              return (
                <div key={index}>
                  <div
                    className={cn(styles.RequirementsNpaItemBlock, {
                      [styles.RequirementsNpaItemBlockSelected]: isSelected(value),
                    })}
                    onClick={() => handleSelect(value)}
                  >
                    <div className={styles.NpaBlock}>
                      <div className={styles.Header}>
                        <span className={styles.GreyHeader}>НПА №</span>
                        <span>{value.npa.number}</span>
                        <span className={styles.GreyHeader}>от</span>
                        <span>{value.npa.acceptanceDate}</span>
                      </div>
                      {value.npa.title}
                    </div>
                    <div className={styles.StructuralUnitBlock}>
                      <div className={styles.Header}>
                        <span className={styles.GreyHeader}>Структурная единица</span>
                      </div>
                      {value.structuralUnit.title}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <div className={styles.FilterNotFound}>Ничего не найдено</div>
        )}
      </ModalBody>
      <ModalActions>
        <Button
          text="Добавить"
          color="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={!selectedRequirementsNpa.length}
        />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
